// Device-local face enrollments: the descriptors face unlock matches against, plus the sealed
// PIN it replays to mint a session. Mirrors the profile-roster-store pattern (module singleton
// + localStorage + change event).
//
// Enrollments are per-user and device-local by design — nothing here is ever sent to the server,
// which is what "store faces vector locally" buys: a descriptor is biometric data, and keeping
// it on the machine that captured it keeps it out of the backend's PHI surface entirely.
//
// The sealed PIN is the sharp edge. See secret-vault.js — today it is obfuscated, not encrypted.
// removeProfile() in profile-roster-store.js calls removeFaceEnrollment so that "remove from
// this device" forgets the face and the credential together; forgetting one without the other
// would leave a replayable PIN behind for a profile the user believes is gone.

import { sealSecret, unsealSecret } from "@/lib/face/secret-vault";

const ENROLLMENTS_STORAGE_KEY = "healo-face-enrollments";
const ENROLLMENTS_CHANGE_EVENT = "healo-face-enrollments-change";

// Human's faceres model emits a 1024-dimension embedding.
const DESCRIPTOR_LENGTH = 1024;
// Stored at 6 decimal places: embeddings are unit-normalized, so the truncation is far below
// the matcher's tolerance, and it roughly halves the localStorage footprint of each sample.
const DESCRIPTOR_PRECISION = 6;

let cache = null;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function nowIso() {
  return new Date().toISOString();
}

function normalizeDescriptor(value) {
  // Float32Array (straight off the model) and plain arrays (out of JSON) both land here.
  const list = ArrayBuffer.isView(value) ? Array.from(value) : value;
  if (!Array.isArray(list) || list.length !== DESCRIPTOR_LENGTH) return null;
  const result = new Array(DESCRIPTOR_LENGTH);
  for (let i = 0; i < DESCRIPTOR_LENGTH; i += 1) {
    const num = Number(list[i]);
    // One NaN would poison every distance computed against this sample, so reject the whole
    // descriptor rather than coercing it to zero and silently degrading match quality.
    if (!Number.isFinite(num)) return null;
    result[i] = Number(num.toFixed(DESCRIPTOR_PRECISION));
  }
  return result;
}

function normalizeEnrollment(value) {
  if (!value || typeof value !== "object") return null;
  const userId = String(value.userId || "").trim();
  const loginId = String(value.loginId || "").trim().toLowerCase();
  if (!userId || !loginId) return null;

  const descriptors = Array.isArray(value.descriptors)
    ? value.descriptors.map(normalizeDescriptor).filter(Boolean)
    : [];
  // An enrollment with no usable descriptor can never match, and keeping it would leave the
  // sealed PIN sitting in storage guarding nothing.
  if (!descriptors.length) return null;

  return {
    userId,
    loginId,
    displayName: String(value.displayName || "").trim() || loginId,
    descriptors,
    sealedPin: String(value.sealedPin || ""),
    enrolledAt: String(value.enrolledAt || "").trim() || nowIso(),
  };
}

function normalizeEnrollmentList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const entry of value) {
    const enrollment = normalizeEnrollment(entry);
    if (!enrollment || seen.has(enrollment.userId)) continue;
    seen.add(enrollment.userId);
    result.push(enrollment);
  }
  return result;
}

function readFromStorage() {
  if (!canUseBrowserStorage()) return [];
  try {
    const raw = window.localStorage.getItem(ENROLLMENTS_STORAGE_KEY);
    if (!raw) return [];
    return normalizeEnrollmentList(JSON.parse(raw));
  } catch {
    return [];
  }
}

function loadEnrollments() {
  if (cache) return cache;
  cache = readFromStorage();
  return cache;
}

function persist(enrollments) {
  if (!canUseBrowserStorage()) return;
  try {
    window.localStorage.setItem(ENROLLMENTS_STORAGE_KEY, JSON.stringify(enrollments));
  } catch {
    /* ignore quota / serialization errors */
  }
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(ENROLLMENTS_CHANGE_EVENT));
}

function setEnrollments(next) {
  cache = normalizeEnrollmentList(next);
  persist(cache);
  emitChange();
  return cache;
}

/** Every enrollment on this device. Descriptors included — the matcher needs them each frame. */
export function getEnrollments() {
  return loadEnrollments();
}

export function getEnrollment(userId) {
  const id = String(userId || "").trim();
  if (!id) return null;
  return loadEnrollments().find((e) => e.userId === id) || null;
}

export function hasEnrollment(userId) {
  return Boolean(getEnrollment(userId));
}

/**
 * Enroll (or re-enroll) a user's face. `pin` is sealed before it touches storage; passing an
 * empty pin stores the descriptors with no credential, which lets the face be *recognized*
 * (enough to detect a stranger) but never to mint a session on its own.
 * Re-enrolling replaces prior samples outright rather than appending — a user re-enrolls
 * because the old samples match poorly, so keeping them would preserve the problem.
 */
export async function saveEnrollment({ userId, loginId, displayName, descriptors, pin }) {
  const sealedPin = await sealSecret(pin);
  const enrollment = normalizeEnrollment({
    userId,
    loginId,
    displayName,
    descriptors,
    sealedPin,
    enrolledAt: nowIso(),
  });
  if (!enrollment) return getEnrollments();
  const rest = loadEnrollments().filter((e) => e.userId !== enrollment.userId);
  return setEnrollments([...rest, enrollment]);
}

/**
 * The PIN for a recognized user, or '' when there is none / it cannot be unsealed. Callers
 * treat '' as "recognized but cannot sign in unattended" and fall back to the PIN prompt.
 */
export async function getEnrolledPin(userId) {
  const enrollment = getEnrollment(userId);
  if (!enrollment?.sealedPin) return "";
  return unsealSecret(enrollment.sealedPin);
}

/** Forget one user's face + sealed PIN. Also called by removeProfile (remove from device). */
export function removeFaceEnrollment(userId) {
  const id = String(userId || "").trim();
  if (!id) return getEnrollments();
  return setEnrollments(loadEnrollments().filter((e) => e.userId !== id));
}

/** Forget every enrollment — the settings "turn it all off and forget faces" escape hatch. */
export function clearAllFaceEnrollments() {
  return setEnrollments([]);
}

export function subscribeFaceEnrollments(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getEnrollments());
  window.addEventListener(ENROLLMENTS_CHANGE_EVENT, handler);
  return () => window.removeEventListener(ENROLLMENTS_CHANGE_EVENT, handler);
}
